"use client";
import Room, { RoomProps } from "@/lib/components/Room";
import SearchBar, { OnSearchProps } from "@/lib/components/SearchBar";
import { useLanguage } from "@/lib/hooks/useLanguage";
import { useTailwindBreakpoint } from "@/lib/hooks/useTailwindBreakpoint";
import {
    convertToListing,
    extractFilterUrlParameters,
    extractSearchUrlParameters,
    generateSearchUrl,
    getCoordinateCenter,
    getCityFromURL,
} from "@/lib/utils/search";
import { useRouter, useSearchParams } from "next/navigation";
import { MutableRefObject, ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import SearchFilter from "./SearchFilter";
import { useWindowSize } from "@uidotdev/usehooks";
import NoResults from "./NoResults";
import SearchMap from "@/lib/components/SearchMap";
// import Text from "@/lib/components/Text";
import { useTranslation } from "@/lib/i18n/client";
import SortByFilter from "./SortBy";
import { SearchLocation } from "@/lib/utils/googleMapsUtils";
import { SCREEN_SIZE } from "@/lib/constants/tailwind";
import Button from "@/lib/components/Button";
import { classNames } from "@/lib/utils/general";
import { faMap } from "@fortawesome/pro-regular-svg-icons";
import Input from "@/lib/components/Input";
import { breakpointIsGreaterThanOrEqualTo, breakpointIsLessThan } from "@/lib/utils/tailwind";
import SkeletonRoom from "@/lib/components/Room/SkeletonRoom";
import { Coordinates } from "@/lib/types/Map";
import { PagingSection } from "@/lib/components/Pagination";
import { Filter, SortType, defaultMithobhai } from "@/lib/types/Search";
import { SEARCH_API_DATE_FORMAT, googleMapsDefaultLocation } from "@/lib/constants/search";
import { API_SEARCH } from "@/lib/services/searchService";
import { PopularCity } from "@/lib/types/PopularCity";
import { useGoogleMapsPlaces } from "@/lib/hooks/useGoogleMapsPlaces";
import { useAtomValue } from "jotai";
import { authAtom } from "@/lib/jotai/auth/authStore";
// import Link from "next/link";
import { Urls, getUrl } from "@/lib/constants/urls";
import { GoogleTagManagerEvents, RoomItem } from "@/lib/utils/googleTagEvents";
import { differenceInDays, format } from "date-fns";
import Breadcrumbs from "@/lib/components/Breadcrumbs";
import CloudImage from "@/lib/components/CloudImage";
import { CLOUDINARY_FOLDER } from "@/lib/constants/cloudinary";
import Text from "@/lib/components/Text";
import Link from "@/lib/components/Link";

type SearchSectionProps = {
    discountBanner: ReactElement | undefined;
    mostPopular: PopularCity[] | undefined;
};

function hasDiscount(room: RoomProps): boolean {
    return (
        room.originalPricePerMonth !== undefined &&
        room.originalPricePerMonth > room.pricePerMonth &&
        !room.isSpecialBusinessOffer
    );
}

function getDefaultOrderPriority(room: RoomProps): number {
    const isInstantBooking = room.isInstantBooking;
    const isFeatured = room.isFeatured;
    const discount = hasDiscount(room);

    if (isInstantBooking && isFeatured && discount) {
        return 1;
    } else if (isInstantBooking && isFeatured) {
        return 2;
    } else if (isInstantBooking) {
        return 3;
    } else if (isFeatured) {
        return 4;
    } else {
        return 5;
    }
}

function getFeaturedOrderPriority(room: RoomProps): number {
    const isInstantBooking = room.isInstantBooking;
    const isFeatured = room.isFeatured;
    const discount = hasDiscount(room);

    if (isInstantBooking && isFeatured && discount) {
        return 1;
    } else if (isInstantBooking && isFeatured) {
        return 2;
    } else if (isFeatured) {
        return 3;
    } else {
        return 4;
    }
}

const SearchSection = ({ discountBanner, mostPopular }: SearchSectionProps) => {
    const [rooms, setRooms] = useState<RoomProps[]>([]);
    const [nrOfRooms, setNrOfRooms] = useState<number>(0);
    const [pageNr, setPageNr] = useState<number>(1);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, currentBreakpoint] = useTailwindBreakpoint();
    const [isMapVisible, setIsMapVisible] = useState(false);
    const searchUrlParams = useSearchParams();
    const [searchBarProps, setSearchBarProps] = useState(extractSearchUrlParameters(searchUrlParams));
    const pageSize = 15;
    const lang = useLanguage();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const router = useRouter();
    const [currentRooms, setCurrentRooms] = useState<RoomProps[]>([]);
    const [filter, setFilter] = useState<Filter>(extractFilterUrlParameters(searchUrlParams));
    const size = useWindowSize();
    const { t } = useTranslation(lang, "search");
    const { t: citiesT } = useTranslation(lang, "cities-description");
    const [sortBy, setSortBy] = useState<SortType>("price_asc");
    const DESKTOP_BREAKPOINT: SCREEN_SIZE = "xl";
    const [isLoading, setIsLoading] = useState(true);
    const [googleMapsCenter, setGoogleMapsCenter] = useState<Coordinates | null>(null);
    const refSkipSetCoordinates = useRef(false);
    const [selectedRoom, setSelectedRoom] = useState<RoomProps | null>(null);
    const [selectedRoomMouseHover, setSelectedRoomMouseHover] = useState<RoomProps | null>(null);
    const [roomsPerRow, setRoomsPerRow] = useState(2);
    const [noResultsLocation, setNoResultsLocation] = useState<string | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const placesService = useGoogleMapsPlaces(map);
    const { isLoggedIn, userInfo } = useAtomValue(authAtom);

    const userIdForGTM = isLoggedIn ? userInfo._id : undefined;

    const cityRaw = getCityFromURL(); // Retrieve the city from the URL
    const currentCity = cityRaw ? cityRaw.toLowerCase() : "berlin"; // Convert it to lowercase
    

    const cityDatalocation = `${citiesT(`cities.${currentCity}.name`, { defaultValue: currentCity })}, ${citiesT(`cities.${currentCity}.country`, { defaultValue: currentCity })}`;
    const cityDatacountry = `${citiesT(`cities.${currentCity}.country`, { defaultValue: currentCity })}`;
    const cityName = `${citiesT(`cities.${currentCity}.name`, { defaultValue: currentCity })}`;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const searchParams = new URLSearchParams({
        location: cityDatalocation ?? "",
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const countrySearchParams = new URLSearchParams({
        location: cityDatacountry,
    });


    

    const skeletonRooms = Array(pageSize)
        .fill(0)
        .map((_, i) => {
            return <SkeletonRoom key={i} />;
        });

    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(breakpointIsLessThan(currentBreakpoint, DESKTOP_BREAKPOINT));

    const openMobileSearch = () => {
        setIsMobileSearchOpen(true);
    };

    const listingRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<HTMLDivElement | null>(null);
    const listingsScrollRef = useRef<HTMLDivElement | null>(null);

    const resetScroll = () => {
        listingsScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        window.scrollTo({ top: 0 });
    };

    const updatePageNr = (newPage: number) => {
        setPageNr(newPage);
        resetScroll();
    };

    // this is used in case there are no results, but we want to center map to the searched location
    useEffect(() => {
        if (placesService && noResultsLocation) {
            placesService?.findPlaceFromQuery(
                {
                    query: noResultsLocation,
                    fields: ["name", "geometry"],
                },
                (result, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && result && result.length > 0) {
                        setGoogleMapsCenter({
                            lat: result[0].geometry?.location?.lat() ?? googleMapsDefaultLocation.lat,
                            lng: result[0].geometry?.location?.lng() ?? googleMapsDefaultLocation.lng,
                        });
                    }
                },
            );
        }
    }, [placesService, noResultsLocation]);

    const renderDiscountBanner = (): ReactNode => {
        if (isLoggedIn) {
            return discountBanner;
        } else {
            return <Link href={getUrl(Urls.register.index, lang)}>{discountBanner}</Link>;
        }
    };

    const startSearch = async ({ location, checkIn, checkOut, guests }: OnSearchProps) => {
      console.log("yeh startSearch ka button press hoa search page se");
      
        setIsMobileSearchOpen(false);
        setPageNr(1);
        defaultMithobhai.firsttimeload = false; // user search ka button press ker k pohncha search page ka, means first laod ni ha sirect search page ka
        //setIsMapVisible(!breakpointIsLessThan(currentBreakpoint, DESKTOP_BREAKPOINT)); // displayed map on button press becasue now its search page not city listing page anymore
        setSearchBarProps({
            language: lang,
            startDate: checkIn,
            endDate: checkOut,
            location: location,
            guestCount: guests?.guests,
            ...filter,
        });
    };

    const onMapLocationChange = (loc: SearchLocation) => {
        refSkipSetCoordinates.current = true;
        setPageNr(1);
        setSearchBarProps({
            ...searchBarProps,
            location: loc,
        });
    };

    const onMapListingClick = (listing: RoomProps) => {
        setSelectedRoom(listing);
    };

    const updateNumberOfRoomsPerRow = (listingRef: MutableRefObject<HTMLDivElement | null>) => {
        if (listingRef.current) {
            // set number of rooms in row of a grid
            const roomContainerWidth = listingRef.current.clientWidth;

            const containerGap = 32; // gap = 2rem
            const roomMinWidth = 320 + containerGap;
            const containerPadding = 40;

            const numRoomsInRow = Math.floor(Math.max(1, (roomContainerWidth - containerPadding) / roomMinWidth));
            setRoomsPerRow(numRoomsInRow);
        }
    };

    useEffect(() => {
        // Carryover of the Ad Reveneu Google and other query string paramas
        const carryOverParams: { [key: string]: string } = {};
        for (const [key, value] of searchUrlParams.entries()) {
          console.log("caryover ban rha with key", carryOverParams[key]);
          console.log("caryover ban rha with value", value);
            carryOverParams[key] = value;
        }

        const cityName = getCityFromURL(); // Get the city name from the URL
        const finalLocation = cityName; // Manually narrow types

        console.log("yeh ha new varr:", defaultMithobhai.firsttimeload);
        console.log("yeh ha carryover prams:", carryOverParams);
        console.log("yeh ha filter prams:", filter);
        console.log("yeh ha searchBarProps prams:", searchBarProps);
        console.log("yeh ha finalLocation prams:", finalLocation);

        
        const searchUrl = generateSearchUrl({
            language: lang,
            ...carryOverParams,
            ...filter,
            ...searchBarProps,
        });

      const urlString = searchUrl;
      const updatedUrlString = urlString.replace("/?location=", "/");
      // Replace the first instance of "&" with "?"
      const updatedUrlStringAnd = updatedUrlString.replace("&", "?");

//console.log(updatedUrlString);
        console.log("yeh search url bna ha ise change", updatedUrlStringAnd);
        //push prams to url if its not citly listing page
        if (defaultMithobhai.firsttimeload === false) {
          router.push(updatedUrlStringAnd); //yeh temp bnd kia hoa tha, yhi url main parameters push krta
        }
        

        //defaultMithobhai.firsttimeload = false;

        console.log("yeh ha new varr after load: ", defaultMithobhai.firsttimeload);

        async function search() {
            try {
                setIsLoading(true);
                setNoResultsLocation(null);
                setCurrentRooms([]);

                const searchResponse = await API_SEARCH.Search(
                    {
                        ...searchBarProps,
                        paginationSkip: 0, // pagination is currently only handled client-side
                        paginationLimit: 1000,
                        sort: sortBy,
                        priceFrom: 0,
                        language: lang,
                    },
                    filter,
                );

                const roomDataConverted: RoomProps[] =
                    searchResponse?.results.map((resultRoom) => convertToListing(resultRoom, lang)) ?? [];
                const roomData = roomDataConverted.sort((a, b) => {
                    const priorityA = getDefaultOrderPriority(a);
                    const priorityB = getDefaultOrderPriority(b);

                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    } else {
                        return a.pricePerMonth - b.pricePerMonth;
                    }
                });
                if (roomData.length === 0) {
                    setNoResultsLocation(searchBarProps?.location?.name ?? "");
                }

                setNrOfRooms(roomData.length);

                const checkIn = format(new Date(searchBarProps.startDate ?? ""), SEARCH_API_DATE_FORMAT);
                const checkOut = format(new Date(searchBarProps.endDate ?? ""), SEARCH_API_DATE_FORMAT);

                const bookingInDays = differenceInDays(
                    searchBarProps.endDate as Date,
                    searchBarProps.startDate as Date,
                );

                const roomItems: RoomItem[] =
                    searchResponse?.results.map((resultRoom) => {
                        return {
                            item_id: resultRoom.roomCategory._id,
                            hotel_name: resultRoom.property.name ?? "",
                            hotel_type: resultRoom.roomCategory.roomType,
                            hotel_room_name: resultRoom.roomCategory.name,
                            hotel_city: resultRoom.address.city ?? searchBarProps.location?.name,
                            price: resultRoom.roomCategory.pricing.averageMonthly / 100,
                        } as unknown as RoomItem;
                    }) ?? [];

                GoogleTagManagerEvents.viewSearchResultsPage(
                    lang,
                    "search_result_page",
                    searchBarProps?.location?.name ?? "",
                    searchUrl,
                    checkIn,
                    checkOut,
                    searchBarProps.bookingType === undefined ? "typebooking_request" : "instant_request",
                    searchBarProps.guestCount,
                    bookingInDays,
                    roomData.length,
                    roomItems,
                    isLoggedIn ? userInfo._id : undefined,
                    "EUR",
                );

                setRooms(roomData);
                const roomsToShow = roomData.filter((_, i) => i < pageSize);
                setCurrentRooms(roomsToShow);

                if (refSkipSetCoordinates && refSkipSetCoordinates.current === true) {
                    refSkipSetCoordinates.current = false;
                } else if (roomData.length > 0) {
                    setGoogleMapsCenter(getCoordinateCenter(roomData));
                }

                resetScroll();
            } catch (error) {
                setNoResultsLocation("An error occurred. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        }

        search();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, searchBarProps, lang, router, isLoggedIn]);

    const handleSorting = (sortBy: SortType) => {
        setIsLoading(true);
        const sorted = [...rooms];

        if (sortBy === "price_asc") {
            sorted.sort((a, b) => a.pricePerMonth - b.pricePerMonth);
        } else if (sortBy === "price_desc") {
            sorted.sort((a, b) => b.pricePerMonth - a.pricePerMonth);
        } else if (sortBy === "default") {
            sorted.sort((a, b) => {
                const priorityA = getDefaultOrderPriority(a);
                const priorityB = getDefaultOrderPriority(b);

                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                } else {
                    return a.pricePerMonth - b.pricePerMonth;
                }
            });
        } else if (sortBy === "popular") {
            sorted.sort((a, b) => {
                const priorityA = getFeaturedOrderPriority(a);
                const priorityB = getFeaturedOrderPriority(b);

                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                } else {
                    return a.pricePerMonth - b.pricePerMonth;
                }
            });
        }

        setRooms(sorted);
        setIsLoading(false);
        setSortBy(sortBy);
    };

    useEffect(() => {
      if (defaultMithobhai.firsttimeload === true) {
        console.log("hide map by defaul if city page"); // hide map by default if city page
        //setIsMapVisible(!breakpointIsLessThan(currentBreakpoint, DESKTOP_BREAKPOINT));
      }else{
        console.log("hide map by defaul"); // hide map by default anyways
        //setIsMapVisible(!breakpointIsLessThan(currentBreakpoint, DESKTOP_BREAKPOINT));
      }
        //setIsMapVisible(!breakpointIsLessThan(currentBreakpoint, DESKTOP_BREAKPOINT));
        setIsMobile(breakpointIsLessThan(currentBreakpoint, DESKTOP_BREAKPOINT));
    }, [currentBreakpoint]);

    useEffect(() => {
        if (listingRef.current) {
            // Safari hack that removes all scrolling from body - otherwise scrollbars appear here
            document.body.classList.add("overflow-hidden");
            // Elements with overflow:scroll need to have height defined (here: https://www.w3schools.com/css/css_overflow.asp)
            listingRef.current.style.height = `${window.innerHeight - listingRef.current.offsetTop}px`;
            listingRef.current.style.display = isLoading || (!isLoading && currentRooms.length > 0) ? "" : "hidden";
            updateNumberOfRoomsPerRow(listingRef);
        }
        if (mapRef.current) {
            mapRef.current.style.height = `${window.innerHeight - mapRef.current.offsetTop}px`;
        }
    }, [size, currentBreakpoint, isLoading, currentRooms, isMapVisible]);

    useEffect(() => {
        const minIdx = (pageNr - 1) * pageSize;
        const maxIdx = pageNr * pageSize;
        const roomsToShow = rooms.filter((_, i) => i >= minIdx && i < maxIdx);
        setCurrentRooms(roomsToShow);
    }, [rooms, pageNr]);

    // reverse of Safari hack that removes all scrolling from body
    useEffect(() => {
        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, []);

    const handleSelectRoom = (room: RoomProps) => {
        setSelectedRoom(room);
    };

    const handleSelectRoomMouseOver = (room: RoomProps) => {
        setSelectedRoomMouseHover(room);
    };

    const updatedFilter = {
        ...filter,
        price_from:
            currentRooms.length > 0 ? Math.round(Math.min(...rooms.map((room) => room.pricePerMonth / 100))) : 0,
        price_to: currentRooms.length > 0 ? Math.round(Math.max(...rooms.map((room) => room.pricePerMonth / 100))) : 0,
    };


    const cityNameTranslation = citiesT(`cities.${currentCity}.name`);
    const cityCountryURL = getUrl(Urls.home.index, lang) + "s/"+ cityDatacountry.toString();
    const cityNameURL =  getUrl(Urls.home.index, lang) + "s/"+ (cityRaw || "").toString();

    // Check if the translation exists and is not the same as the key
    const isTranslationValid =
    cityNameTranslation &&
    cityNameTranslation.trim() &&
    cityNameTranslation !== `cities.${currentCity}.name`;
    


    return (
        <div className="flex flex-row h-full">
            <div className="w-full bg-gray-100 h-full flex flex-row relative">
                <div
                    className={classNames(
                        "w-full ",
                        isMapVisible ? " basis-7/12 " : " ",
                        isMobile && isMapVisible ? " hidden " : " ",
                    )}
                >
                    <div
                        className={`h-16 lg:h-28 xl:h-32 bg-white px-3  xl:px-9 mt-header-small lg:mt-header flex flex-row items-center ${classNames(
                            !isMapVisible && "justify-between",
                            isMobile && "pb-2 shadow-md",
                        )}`}
                    >
                        <div className="lg:w-full w-full xl:px-6 px-2 xl:hidden flex flex-row gap-5">
                            <Input
                                placeholder={t("where-to-go") ?? ""}
                                className="w-full"
                                onClick={openMobileSearch}
                                readOnly
                                value={searchUrlParams.get("location") ?? (getCityFromURL() || "").toString()}
                            />
                            <SearchFilter
                                defaultFilter={updatedFilter}
                                onFilter={setFilter}
                                rooms={rooms}
                                ratesMin={
                                    currentRooms.length > 0
                                        ? Math.round(Math.min(...currentRooms.map((room) => room.pricePerMonth / 100)))
                                        : 0
                                }
                                ratesMax={
                                    currentRooms.length > 0
                                        ? Math.round(Math.max(...currentRooms.map((room) => room.pricePerMonth / 100)))
                                        : 0
                                }
                                modalFullScreenBreakpoint={DESKTOP_BREAKPOINT}
                            />
                        </div>
                        <SearchBar
                            isOnResultsPage
                            isLoading={isLoading}
                            className={`hidden w-full testabdu ${classNames(
                                !isMapVisible && "basis-7/12",
                                `${DESKTOP_BREAKPOINT}:block`,
                            )}`}
                            onSearch={startSearch}
                            defaultProps={searchBarProps}
                            onCloseMobileModal={() => setIsMobileSearchOpen(false)}
                            isMobileModalOpen={isMobileSearchOpen}
                        >
                            {breakpointIsGreaterThanOrEqualTo(currentBreakpoint, DESKTOP_BREAKPOINT) && (
                                <SearchFilter
                                    defaultFilter={updatedFilter}
                                    onFilter={setFilter}
                                    ratesMin={
                                        currentRooms.length > 0
                                            ? Math.round(
                                                  Math.min(...currentRooms.map((room) => room.pricePerMonth / 100)),
                                              )
                                            : 0
                                    }
                                    ratesMax={
                                        currentRooms.length > 0
                                            ? Math.round(
                                                  Math.max(...currentRooms.map((room) => room.pricePerMonth / 100)),
                                              )
                                            : 0
                                    }
                                    rooms={rooms}
                                    modalFullScreenBreakpoint={DESKTOP_BREAKPOINT}
                                />
                            )}
                        </SearchBar>
                        {!isMapVisible && (
                            <Button
                                icon={faMap}
                                variant="white"
                                className="hidden p-3 xl:block"
                                onClick={() => {
                                    GoogleTagManagerEvents.clickOnMaximizeMapButton(lang, userIdForGTM);
                                    setIsMapVisible(true);
                                }}
                            >
                                {t("show-map")}
                            </Button>
                        )}
                    </div>
                    {!isMobile && currentRooms.length > 0 && discountBanner && (
                        <div className="sm:px-9 px-5">{renderDiscountBanner()}</div>
                    )}
                    {!isLoading && currentRooms.length > 0 && (
                        <div className="w-full flex flex-row  sm:px-9 px-5 justify-between text-base py-2">
                            <Text as="span">{`${nrOfRooms} ${t("num-results")}`} {searchUrlParams.get("location") ?? (getCityFromURL() || "").toString().replace(/^./, char => char.toUpperCase())}</Text>
                            <div className="flex flex-row gap-1">
                                <SortByFilter lang={lang} onChange={handleSorting} />
                            </div>
                        </div>
                    )}
                    {isLoading && (
                        <div className="h-9 flex mb-1 mx-6 md:mx-9 bg-gray-200 rounded-md animate-pulse"></div>
                    )}
                    {!isLoading && currentRooms.length === 0 && (
                        <div className="mt-12 mithocheck1">
                            <NoResults mostPopular={mostPopular} />
                        </div>
                    )}
                    <div className="flex flex-row mithocheck2" ref={listingRef}>
                        <div className="w-full">
                            <div
                                className={`${
                                    isMapVisible ? "basis-1/2 w-full" : ""
                                } min-h-0 h-full overflow-scroll sm:pl-9 sm:pr-7 px-5`}
                                ref={listingsScrollRef}
                            >
                                {isMobile && !isLoading && currentRooms.length > 0 && (
                                    <div className="checkbannerAB hidden">{renderDiscountBanner()}</div>
                                )}
                                {isLoading && (
                                    <div className={`skeleton-grid grid gap-4 xl:gap-8 grid-cols-${roomsPerRow}`}>
                                        {skeletonRooms}
                                    </div>
                                )}
                                <div className={`grid gap-4 xl:gap-8 grid-cols-${roomsPerRow}`}>
                                    {!isLoading &&
                                        currentRooms.length > 0 &&
                                        currentRooms.map((room) => (
                                            <div
                                                key={`${room.roomId}`}
                                                data-room-id={room.roomId}
                                                onMouseOver={() => handleSelectRoomMouseOver(room)}
                                                onClick={() => handleSelectRoom(room)}
                                            >
                                                <Room
                                                    {...room}
                                                    className={`border-2 hover:bg-cyan-500 ${
                                                        room.roomId === selectedRoomMouseHover?.roomId
                                                            ? "border-cyan-500"
                                                            : "border-transparent"
                                                    }`}
                                                />
                                            </div>
                                        ))}
                                </div>
                                {currentRooms.length > 1 || pageNr > 0 ? (
                                    <div className="flex flex-row justify-center md:mt-5">
                                        <div className="relative bg-gray-100 pt-3 z-30 mb-2 pb-4 mt-6">
                                            <PagingSection
                                                maxRecords={nrOfRooms}
                                                selectedPage={pageNr}
                                                changePage={updatePageNr}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                                {isTranslationValid ? (
                                  <div className="citydescription-new pb-20">
                                      {/* Content for when translation exists */}
                                      <Breadcrumbs
                                          links={[
                                              { title: citiesT("home"), url: getUrl(Urls.home.index, lang) },
                                              { title: cityDatacountry, url: cityCountryURL },
                                              { title: cityName, url: cityNameURL },
                                               
                                          ]}
                                          className="pb-4 sm:pb-6 lg:pb-10 xl:pb-14"
                                      />

                                      <div className="flex flex-col lg:flex-row lg:gap-20">
                                          <div className="basis-1/2 relative min-h-[20rem]">
                                              <CloudImage
                                                  src={`${CLOUDINARY_FOLDER}/${citiesT(`cities.${currentCity}.image`, {
                                                      defaultValue: currentCity,
                                                  })}`}
                                                  alt={citiesT(`cities.${currentCity}.name`, { defaultValue: currentCity })}
                                                  className="object-cover rounded-xl"
                                              />
                                          </div>
                                          <div className="basis-1/2 py-4">
                                              <Text
                                                  as="h2"
                                                  className={`font-semibold max-sm:text-xl sm:text-2xl whitespace-break-spaces pb-4 ${
                                                    currentCity === "münchen"
                                                        ? ""
                                                        : "hidden"
                                                }`}
                                              >
                                                  {citiesT(`cities.${currentCity}.headingtext`, { defaultValue: "" })}
                                              </Text>
                                              <Text as="p" className="text-lg mb-8 font-medium leading-10 whitespace-pre-line">
                                                  {/* split each /n in new paragraph if /n present in string */}
                                                  {citiesT(`cities.${currentCity}.text`, { defaultValue: "" })
                                                    .split("\n")
                                                    .map((line, index) => (
                                                      <p key={index}>{line}</p>
                                                    ))}
                                              </Text>
                                              {/* <div className="flex flex-row">
                                                  <Link
                                                      href={cityCountryURL}
                                                      className="px-8"
                                                  >
                                                      {citiesT("show-all-stays")}
                                                  </Link>
                                              </div> */}
                                          </div>
                                      </div>
                                  </div>
                              ) : (
                                  <div className="citydescription-empty">
                                      {/* Content for when translation does not exist */}
                                  </div>
                              )}


                            </div>
                        </div>
                    </div>
                </div>
                <div className={`flex grow mt-header-small lg:mt-header relative overflow-hidden`} ref={mapRef}>
                    <div className={`xl:block grow`}>
                        {isMobile && isMapVisible && (
                            <div className="flex items-center flex-row gap-5 px-5 py-1">
                                <Input
                                    placeholder={t("where-to-go") ?? ""}
                                    className="w-full"
                                    onClick={openMobileSearch}
                                    readOnly
                                    value={searchUrlParams.get("location") ?? (getCityFromURL() || "").toString()}
                                />
                                <SearchFilter
                                    defaultFilter={updatedFilter}
                                    onFilter={setFilter}
                                    ratesMin={
                                        currentRooms.length > 0
                                            ? Math.round(
                                                  Math.min(...currentRooms.map((room) => room.pricePerMonth / 100)),
                                              )
                                            : 0
                                    }
                                    ratesMax={
                                        currentRooms.length > 0
                                            ? Math.round(
                                                  Math.max(...currentRooms.map((room) => room.pricePerMonth / 100)),
                                              )
                                            : 0
                                    }
                                    rooms={rooms}
                                    modalFullScreenBreakpoint={DESKTOP_BREAKPOINT}
                                />
                            </div>
                        )}

                        <SearchMap
                            isLoading={isLoading}
                            coordinates={googleMapsCenter}
                            listings={rooms}
                            onCloseMap={() => setIsMapVisible(false)}
                            onLocationChange={onMapLocationChange}
                            onListingClick={onMapListingClick}
                            isMobile={isMobile}
                            selectedRoom={selectedRoom}
                            selectedRoomMouseHover={selectedRoomMouseHover}
                            isVisible={isMapVisible}
                            onMapSet={(map) => setMap(map)}
                        />
                    </div>
                </div>
                
            </div>
            {isMobile && !isMapVisible && (
                <div className="absolute z-20 left-1/2 bottom-2">
                    <Button
                        variant="black"
                        className="p-4 relative -left-1/2"
                        size="lg"
                        icon={faMap}
                        onClick={() => {
                            GoogleTagManagerEvents.clickOnMaximizeMapButton(lang, userIdForGTM);
                            setIsMapVisible(true);
                        }}
                    >
                        {t("mobile.button-map") ?? ""}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SearchSection;
